import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import TopNav from '../../components/layout/TopNav';
import { useAuthStore } from '../../store';
import api from '../../services/api';
import type { FlagType, EffectiveRule, DealerGroup } from '../../types';
import DmsSettingsPanel from './DmsSettingsPanel';
import Tooltip from '../../components/shared/Tooltip';

/* ---------- CRM Integration types ---------- */
type CrmProvider = 'salesforce' | 'hubspot' | 'vin_solutions' | 'elead';

interface CrmIntegration {
  id: string;
  provider: CrmProvider;
  isActive: boolean;
  lastTestedAt: string | null;
  lastErrorMessage: string | null;
}

const CRM_LABELS: Record<CrmProvider, string> = {
  salesforce: 'Salesforce',
  hubspot: 'HubSpot',
  vin_solutions: 'VinSolutions',
  elead: 'eLead',
};

const CRM_ICONS: Record<CrmProvider, string> = {
  salesforce: 'cloud',
  hubspot: 'hub',
  vin_solutions: 'directions_car',
  elead: 'manage_accounts',
};

const ALL_PROVIDERS: CrmProvider[] = ['salesforce', 'hubspot', 'vin_solutions', 'elead'];

/* ---------- Word list state ---------- */
interface WordListState {
  excludeCompetitorNames: string[];
  discriminatoryPhrases: string[];
  negativeSentimentPhrases: string[];
  profanityWords: string[];
  pricingPatterns: string[];
  fillerWords: string[];
}

/* ---------- Compliance Rule constants ---------- */
const FLAG_TYPE_LABELS: Record<string, { label: string; description: string; severity: 'HARD_BLOCK' | 'SOFT_WARNING' }> = {
  PROFANITY:               { label: 'Profanity Detection',          description: 'Flags videos containing profane language.',                         severity: 'HARD_BLOCK' },
  COMPETITOR_MENTION:      { label: 'Competitor Mention',           description: 'Flags mentions of competitor brands or models.',                     severity: 'HARD_BLOCK' },
  VERBAL_PRICING:          { label: 'Verbal Pricing Claims',        description: 'Flags dollar amounts, APR rates, or payment promises.',              severity: 'HARD_BLOCK' },
  DISCRIMINATORY_LANGUAGE: { label: 'Discriminatory Language',      description: 'Flags fair housing and discriminatory phrases.',                     severity: 'HARD_BLOCK' },
  PII_DETECTED:            { label: 'PII Detection',                description: 'Flags phone numbers, SSNs, or financial identifiers. Locked.',       severity: 'HARD_BLOCK' },
  VIN_MISMATCH:            { label: 'VIN Mismatch',                 description: 'Flags when video VIN doesn\'t match DMS record.',                   severity: 'HARD_BLOCK' },
  NEGATIVE_SENTIMENT:      { label: 'Negative Sentiment',           description: 'Flags phrases that reflect poorly on the dealership.',               severity: 'SOFT_WARNING' },
  LOW_VQS_SCORE:           { label: 'Low VQS Score',                description: 'Flags videos scoring below the configured threshold.',               severity: 'SOFT_WARNING' },
  EXCESSIVE_FILLERS:       { label: 'Excessive Filler Words',       description: 'Flags videos where filler word density exceeds the threshold.',      severity: 'SOFT_WARNING' },
  REQUIRED_PHRASE_MISSING: { label: 'Required Phrase Missing',      description: 'Flags when the rep did not use the required number of configured phrases.', severity: 'HARD_BLOCK' },
};

/* Maps FlagType → the key in WordListState (null = no list for this rule) */
const FLAG_LIST_KEY: Partial<Record<string, keyof WordListState>> = {
  PROFANITY: 'profanityWords',
  COMPETITOR_MENTION: 'excludeCompetitorNames',
  VERBAL_PRICING: 'pricingPatterns',
  DISCRIMINATORY_LANGUAGE: 'discriminatoryPhrases',
  NEGATIVE_SENTIMENT: 'negativeSentimentPhrases',
  EXCESSIVE_FILLERS: 'fillerWords',
};

/* Maps FlagType → PATCH body key for /dealers/me/word-lists */
const FLAG_API_KEY: Partial<Record<string, string>> = {
  PROFANITY: 'profanityWords',
  COMPETITOR_MENTION: 'excludeCompetitorNames',
  VERBAL_PRICING: 'pricingPatterns',
  DISCRIMINATORY_LANGUAGE: 'discriminatoryPhrases',
  NEGATIVE_SENTIMENT: 'negativeSentimentPhrases',
  EXCESSIVE_FILLERS: 'fillerWords',
};

/* Modal metadata per flag type */
const LIST_MODAL_META: Record<string, { title: string; description: string; placeholder: string; saveLabel: string }> = {
  PROFANITY: {
    title: 'Profanity Detection',
    description: 'Manage the list of words flagged as profane. Videos containing any of these words will be blocked from sending.',
    placeholder: 'e.g. badword',
    saveLabel: 'Save Words',
  },
  COMPETITOR_MENTION: {
    title: 'Competitor Detection',
    description: 'Add the brands you represent to exclude them from competitor flagging. Any brand listed here will not trigger a competitor flag.',
    placeholder: 'e.g. Toyota, Ford',
    saveLabel: 'Save Exclusions',
  },
  VERBAL_PRICING: {
    title: 'Pricing Claim Phrases',
    description: 'Manage the phrases that trigger a verbal pricing hard-block. Dollar amounts and percentage rates are always detected automatically regardless of this list.',
    placeholder: 'e.g. zero down, monthly payment of',
    saveLabel: 'Save Phrases',
  },
  DISCRIMINATORY_LANGUAGE: {
    title: 'Discriminatory Language',
    description: 'Manage phrases that trigger a discrimination hard-block. Videos containing any of these phrases will be quarantined.',
    placeholder: 'e.g. discriminatory phrase',
    saveLabel: 'Save Phrases',
  },
  NEGATIVE_SENTIMENT: {
    title: 'Negative Sentiment',
    description: 'Manage phrases that flag a video for negative sentiment. Videos containing these phrases will receive a soft warning.',
    placeholder: 'e.g. sorry about the delay',
    saveLabel: 'Save Phrases',
  },
  EXCESSIVE_FILLERS: {
    title: 'Filler Words',
    description: 'Manage the list of words counted as filler words. A video is flagged when filler density exceeds the threshold.',
    placeholder: 'e.g. um, uh, like',
    saveLabel: 'Save Words',
  },
  REQUIRED_PHRASE_MISSING: {
    title: 'Required Phrases',
    description: 'Define phrases your reps should include in their videos, and set how many must appear for a passing evaluation.',
    placeholder: 'e.g. I\'d love to schedule a test drive',
    saveLabel: 'Save Phrases',
  },
};

/* ---------- Tag Input Component ---------- */
interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  scrollable?: boolean;
  addOnComma?: boolean;
}

const TagInput: React.FC<TagInputProps> = ({ tags, onChange, placeholder, scrollable, addOnComma = true }) => {
  const [input, setInput] = useState('');

  const addTag = () => {
    const val = input.trim();
    if (!val) return;
    if (tags.some((t) => t.toLowerCase() === val.toLowerCase())) {
      setInput('');
      return;
    }
    onChange([...tags, val]);
    setInput('');
  };

  const removeTag = (idx: number) => {
    onChange(tags.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || (addOnComma && e.key === ',')) {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-center">
        <div className="flex-1 space-y-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder ?? 'Type and press Enter'}
            className="h-14 w-full rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-6 text-sm font-bold text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-800 transition-all focus:bg-white dark:focus:bg-slate-900 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none"
          />
        </div>
        <button
          type="button"
          onClick={addTag}
          disabled={!input.trim()}
          className="h-14 px-6 rounded-2xl bg-primary hover:bg-primary/90 text-white font-bold text-xs uppercase tracking-widest transition-all disabled:opacity-30 active:scale-95 shadow-lg shadow-primary/20"
        >
          Add
        </button>
      </div>
      {tags.length > 0 && (
        <div
          className={`flex flex-wrap gap-2 pt-2 ${
            scrollable ? 'max-h-[168px] overflow-y-auto pr-1' : ''
          }`}
        >
          {tags.map((tag, idx) => (
            <span
              key={`${tag}-${idx}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl text-sm font-bold text-slate-700 dark:text-slate-300 shadow-sm animate-in zoom-in-95 duration-200"
            >
              {tag}
              <Tooltip content="Remove">
                <button
                  onClick={() => removeTag(idx)}
                  className="w-5 h-5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-slate-400 hover:text-red-500 transition-all flex items-center justify-center -mr-1"
                >
                  <span className="material-symbols-outlined text-[14px]">close</span>
                </button>
              </Tooltip>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------- List Management Modal ---------- */
interface ListModalProps {
  flagType: FlagType;
  wordLists: WordListState;
  requiredPhrases: string[];
  mandatoryCount: number;
  saving: string | null;
  onSave: (flagType: FlagType, list: string[], mandatory?: number) => Promise<void>;
  onClose: () => void;
}

const ListManagementModal: React.FC<ListModalProps> = ({
  flagType,
  wordLists,
  requiredPhrases,
  mandatoryCount,
  saving,
  onSave,
  onClose,
}) => {
  const isRequiredPhrase = String(flagType) === 'REQUIRED_PHRASE_MISSING';
  const listKey = FLAG_LIST_KEY[String(flagType)];
  const meta = LIST_MODAL_META[String(flagType)];

  const [localList, setLocalList] = useState<string[]>(() =>
    isRequiredPhrase ? [...requiredPhrases] : (listKey ? [...wordLists[listKey]] : []),
  );
  const [localMandatory, setLocalMandatory] = useState(mandatoryCount);

  const isSaving = saving === String(flagType);

  const handleSave = () => {
    if (isRequiredPhrase) {
      onSave(flagType, localList, localMandatory);
    } else {
      onSave(flagType, localList);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100 dark:border-slate-800">
          <div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">{meta?.title}</h3>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 mt-1">
              {isRequiredPhrase ? 'Coaching signal configuration' : 'List management'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-relaxed">
            {meta?.description}
          </p>

          <TagInput
            tags={localList}
            onChange={(tags) => {
              setLocalList(tags);
              if (isRequiredPhrase && localMandatory > tags.length) setLocalMandatory(tags.length);
            }}
            placeholder={meta?.placeholder}
            scrollable
            addOnComma={!isRequiredPhrase}
          />

          {isRequiredPhrase && (
            <div className="space-y-3">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">
                Mandatory Count
                <span className="ml-2 text-slate-300 dark:text-slate-700 normal-case font-medium tracking-normal">
                  (min 0 — max {localList.length})
                </span>
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="number"
                  min={0}
                  max={localList.length}
                  value={localMandatory}
                  onChange={(e) => {
                    const val = Math.max(0, Math.min(localList.length, parseInt(e.target.value, 10) || 0));
                    setLocalMandatory(val);
                  }}
                  className="h-14 w-36 rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-6 text-sm font-bold text-slate-900 dark:text-white transition-all focus:bg-white dark:focus:bg-slate-900 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none"
                />
                {localList.length > 0 && (
                  <span className="text-sm font-medium text-slate-400 dark:text-slate-600">
                    of {localList.length} {localList.length === 1 ? 'phrase' : 'phrases'} required
                  </span>
                )}
              </div>
              {localMandatory > localList.length && (
                <p className="text-xs font-semibold text-red-500 ml-1">
                  Mandatory count cannot exceed the number of phrases.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-8 py-5 border-t border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3 px-4 h-10 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            <span className="text-[10px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest">
              {localList.length} {localList.length === 1 ? 'entry' : 'entries'}
              {isRequiredPhrase && ` · ${localMandatory} mandatory`}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="h-11 px-6 rounded-xl font-bold text-xs uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || (isRequiredPhrase && localMandatory > localList.length)}
              className="h-11 px-8 rounded-xl font-bold text-xs uppercase tracking-widest bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin rounded-full" />
              ) : (
                <>
                  <span className="material-symbols-outlined text-[16px]">save</span>
                  {meta?.saveLabel}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Threshold Modal (Low VQS Score) ---------- */
interface ThresholdModalProps {
  currentThreshold: number;
  saving: string | null;
  onSave: (threshold: number) => Promise<void>;
  onClose: () => void;
}

const ThresholdModal: React.FC<ThresholdModalProps> = ({ currentThreshold, saving, onSave, onClose }) => {
  const [value, setValue] = useState(currentThreshold);
  const isSaving = saving === 'LOW_VQS_SCORE';
  const isValid = Number.isInteger(value) && value >= 0 && value <= 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100 dark:border-slate-800">
          <div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Low VQS Score Threshold</h3>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 mt-1">Soft warning configuration</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6 space-y-6">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-relaxed">
            Videos with a VQS score below this value will receive a soft warning. The default platform threshold is <span className="font-bold text-slate-700 dark:text-slate-300">40</span>.
          </p>
          <div className="space-y-3">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">
              Score Threshold
              <span className="ml-2 text-slate-300 dark:text-slate-700 normal-case font-medium tracking-normal">(0 – 100)</span>
            </label>
            <div className="flex items-center gap-4">
              <input
                type="number"
                min={0}
                max={100}
                value={value}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  setValue(isNaN(n) ? 0 : Math.max(0, Math.min(100, n)));
                }}
                className="h-14 w-40 rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-6 text-sm font-bold text-slate-900 dark:text-white transition-all focus:bg-white dark:focus:bg-slate-900 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none"
              />
              <span className="text-sm font-medium text-slate-400 dark:text-slate-600">out of 100</span>
            </div>
            {!isValid && (
              <p className="text-xs font-semibold text-red-500 ml-1">Enter a whole number between 0 and 100.</p>
            )}
          </div>

          {/* Grade band reference */}
          <div className="grid grid-cols-5 gap-1.5 pt-1">
            {[
              { grade: 'F', range: '0–39', color: 'bg-red-100 dark:bg-red-950/30 text-red-500' },
              { grade: 'D', range: '40–59', color: 'bg-orange-100 dark:bg-orange-950/30 text-orange-500' },
              { grade: 'C', range: '60–74', color: 'bg-amber-100 dark:bg-amber-950/30 text-amber-500' },
              { grade: 'B', range: '75–89', color: 'bg-emerald-100 dark:bg-emerald-950/30 text-emerald-500' },
              { grade: 'A', range: '90–100', color: 'bg-primary/10 dark:bg-primary/20 text-primary' },
            ].map(({ grade, range, color }) => (
              <div key={grade} className={`flex flex-col items-center py-2 px-1 rounded-xl ${color}`}>
                <span className="text-sm font-black">{grade}</span>
                <span className="text-[9px] font-bold opacity-70 mt-0.5">{range}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-slate-100 dark:border-slate-800">
          <button
            onClick={onClose}
            className="h-11 px-6 rounded-xl font-bold text-xs uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(value)}
            disabled={isSaving || !isValid}
            className="h-11 px-8 rounded-xl font-bold text-xs uppercase tracking-widest bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin rounded-full" />
            ) : (
              <>
                <span className="material-symbols-outlined text-[16px]">save</span>
                Save Threshold
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------- Settings Page ---------- */
const SettingsPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const [customRulesEnabled, setCustomRulesEnabled] = useState(false);

  const [wordLists, setWordLists] = useState<WordListState>({
    excludeCompetitorNames: [],
    discriminatoryPhrases: [],
    negativeSentimentPhrases: [],
    profanityWords: [],
    pricingPatterns: [],
    fillerWords: [],
  });
  const [listsLoading, setListsLoading] = useState(true);

  const [requiredPhrases, setRequiredPhrases] = useState<string[]>([]);
  const [mandatoryCount, setMandatoryCount] = useState(0);

  const [effectiveRules, setEffectiveRules] = useState<EffectiveRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [rulesToggling, setRulesToggling] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const [activeModal, setActiveModal] = useState<FlagType | null>(null);
  const [thresholdModalOpen, setThresholdModalOpen] = useState(false);

  const [dealerGroup, setDealerGroup] = useState<DealerGroup | null>(null);
  const [dealerGroupLoading, setDealerGroupLoading] = useState(true);

  const [crmIntegrations, setCrmIntegrations] = useState<CrmIntegration[]>([]);
  const [crmLoading, setCrmLoading] = useState(true);
  const [crmConnectProvider, setCrmConnectProvider] = useState<CrmProvider | null>(null);
  const [crmCredentials, setCrmCredentials] = useState('');
  const [crmSaving, setCrmSaving] = useState<string | null>(null);
  const [crmTesting, setCrmTesting] = useState<string | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    if (type === 'success') toast.success(message);
    else toast.error(message);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/dealers/me');
        const data = res.data.data ?? res.data;
        setCustomRulesEnabled(data?.settings?.featureFlags?.dynamicComplianceRules === true);
      } catch {
        // non-critical
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/dealers/me/dealer-group');
        setDealerGroup(res.data.data ?? null);
      } catch {
        // not critical
      } finally {
        setDealerGroupLoading(false);
      }
    })();
  }, []);

  // Fetch all editable word lists and required phrases in parallel
  useEffect(() => {
    (async () => {
      try {
        const [listsRes, phrasesRes] = await Promise.all([
          api.get('/dealers/me/word-lists'),
          api.get('/dealers/me/required-phrases'),
        ]);
        const lists = listsRes.data.data ?? listsRes.data;
        const phrases = phrasesRes.data.data ?? phrasesRes.data;
        setWordLists({
          excludeCompetitorNames: lists.excludeCompetitorNames ?? [],
          discriminatoryPhrases: lists.discriminatoryPhrases ?? [],
          negativeSentimentPhrases: lists.negativeSentimentPhrases ?? [],
          profanityWords: lists.profanityWords ?? [],
          pricingPatterns: lists.pricingPatterns ?? [],
          fillerWords: lists.fillerWords ?? [],
        });
        setRequiredPhrases(phrases.phrases ?? []);
        setMandatoryCount(phrases.mandatoryCount ?? 0);
      } catch {
        // silently handle — lists default to empty
      } finally {
        setListsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/compliance-rules/effective');
        setEffectiveRules(res.data.data ?? []);
      } catch {
        // silently handle
      } finally {
        setRulesLoading(false);
      }
    })();
  }, []);

  const handleRuleToggle = async (flagType: FlagType, currentEnabled: boolean) => {
    setRulesToggling(String(flagType));
    try {
      await api.put(`/compliance-rules/${flagType}`, { isEnabled: !currentEnabled });
      setEffectiveRules((prev) => {
        const exists = prev.some((r) => r.flagType === flagType);
        if (exists) {
          return prev.map((r) =>
            r.flagType === flagType ? { ...r, isEnabled: !currentEnabled, resolvedFrom: 'TENANT' as const } : r,
          );
        }
        return [
          ...prev,
          { id: '', flagType, isEnabled: !currentEnabled, config: null, resolvedFrom: 'TENANT' as const, isOverridable: true },
        ];
      });
      showToast(`${FLAG_TYPE_LABELS[String(flagType)]?.label ?? flagType} ${!currentEnabled ? 'enabled' : 'disabled'}.`, 'success');
    } catch {
      showToast('Failed to update rule.', 'error');
    } finally {
      setRulesToggling(null);
    }
  };

  const handleRuleReset = async (flagType: FlagType) => {
    setRulesToggling(String(flagType));
    try {
      await api.delete(`/compliance-rules/${flagType}`);
      const res = await api.get('/compliance-rules/effective');
      setEffectiveRules(res.data.data ?? []);
      showToast('Rule reset to inherited default.', 'success');
    } catch {
      showToast('Failed to reset rule.', 'error');
    } finally {
      setRulesToggling(null);
    }
  };

  const handleSaveList = async (flagType: FlagType, list: string[], mandatory?: number) => {
    setSaving(String(flagType));
    try {
      if (String(flagType) === 'REQUIRED_PHRASE_MISSING') {
        const count = mandatory ?? 0;
        if (count > list.length) {
          showToast(`Mandatory count cannot exceed the number of phrases (${list.length}).`, 'error');
          setSaving(null);
          return;
        }
        const res = await api.patch('/dealers/me/required-phrases', { phrases: list, mandatoryCount: count });
        const data = res.data.data ?? res.data;
        setRequiredPhrases(data.phrases ?? []);
        setMandatoryCount(data.mandatoryCount ?? 0);
      } else {
        const apiKey = FLAG_API_KEY[String(flagType)];
        if (!apiKey) return;
        const res = await api.patch('/dealers/me/word-lists', { [apiKey]: list });
        const data = res.data.data ?? res.data;
        setWordLists({
          excludeCompetitorNames: data.excludeCompetitorNames ?? [],
          discriminatoryPhrases: data.discriminatoryPhrases ?? [],
          negativeSentimentPhrases: data.negativeSentimentPhrases ?? [],
          profanityWords: data.profanityWords ?? [],
          pricingPatterns: data.pricingPatterns ?? [],
          fillerWords: data.fillerWords ?? [],
        });
      }
      setActiveModal(null);
      showToast(`${LIST_MODAL_META[String(flagType)]?.title ?? 'List'} saved.`, 'success');
    } catch {
      showToast('Failed to save. Please try again.', 'error');
    } finally {
      setSaving(null);
    }
  };

  const handleSaveThreshold = async (threshold: number) => {
    setSaving('LOW_VQS_SCORE');
    try {
      const rule = effectiveRules.find((r) => r.flagType === 'LOW_VQS_SCORE');
      const isEnabled = rule?.isEnabled ?? true;
      await api.put('/compliance-rules/LOW_VQS_SCORE', { isEnabled, config: { threshold } });
      setEffectiveRules((prev) => {
        const exists = prev.some((r) => r.flagType === 'LOW_VQS_SCORE');
        if (exists) {
          return prev.map((r) =>
            r.flagType === 'LOW_VQS_SCORE'
              ? { ...r, config: { ...(r.config ?? {}), threshold }, resolvedFrom: 'TENANT' as const }
              : r,
          );
        }
        return [
          ...prev,
          { id: '', flagType: 'LOW_VQS_SCORE' as FlagType, isEnabled, config: { threshold }, resolvedFrom: 'TENANT' as const, isOverridable: true },
        ];
      });
      setThresholdModalOpen(false);
      showToast('VQS score threshold saved.', 'success');
    } catch {
      showToast('Failed to save threshold. Please try again.', 'error');
    } finally {
      setSaving(null);
    }
  };

  // Fetch CRM integrations
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/crm/integrations');
        setCrmIntegrations(res.data.data ?? []);
      } catch {
        // silently handle — CRM is optional
      } finally {
        setCrmLoading(false);
      }
    })();
  }, []);

  const handleCrmConnect = async (provider: CrmProvider) => {
    let credentials: Record<string, unknown>;
    try {
      credentials = JSON.parse(crmCredentials) as Record<string, unknown>;
    } catch {
      showToast('Invalid JSON in credentials field.', 'error');
      return;
    }
    setCrmSaving(provider);
    try {
      const res = await api.post('/crm/integrations', { provider, credentials });
      const updated = res.data.data as CrmIntegration;
      setCrmIntegrations((prev) => {
        const exists = prev.find((i) => i.provider === provider);
        return exists
          ? prev.map((i) => (i.provider === provider ? updated : i))
          : [...prev, updated];
      });
      setCrmConnectProvider(null);
      setCrmCredentials('');
      showToast(`${CRM_LABELS[provider]} connected.`, 'success');
    } catch {
      showToast('Failed to save CRM integration.', 'error');
    } finally {
      setCrmSaving(null);
    }
  };

  const handleCrmTest = async (provider: CrmProvider) => {
    setCrmTesting(provider);
    try {
      const res = await api.post(`/crm/integrations/${provider}/test`);
      const result = res.data.data as { success: boolean; error?: string };
      if (result.success) {
        showToast(`${CRM_LABELS[provider]} connection verified.`, 'success');
        setCrmIntegrations((prev) =>
          prev.map((i) =>
            i.provider === provider
              ? { ...i, lastTestedAt: new Date().toISOString(), lastErrorMessage: null }
              : i,
          ),
        );
      } else {
        showToast(`Test failed: ${result.error ?? 'Unknown error'}`, 'error');
      }
    } catch {
      showToast('Connection test failed.', 'error');
    } finally {
      setCrmTesting(null);
    }
  };

  const handleCrmDisconnect = async (provider: CrmProvider) => {
    setCrmSaving(provider);
    try {
      await api.delete(`/crm/integrations/${provider}`);
      setCrmIntegrations((prev) => prev.filter((i) => i.provider !== provider));
      showToast(`${CRM_LABELS[provider]} disconnected.`, 'success');
    } catch {
      showToast('Failed to disconnect CRM.', 'error');
    } finally {
      setCrmSaving(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-white transition-colors duration-300 font-sans flex flex-col">
      <TopNav />
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">Dealership Intelligence</h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium text-base mt-2 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            Control your dealership configuration and evaluative preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Account Info */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-10 shadow-sm hover:shadow-xl hover:shadow-primary/5 transition-all duration-500 group">
            <div className="flex items-center gap-5 mb-10">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary shadow-inner group-hover:scale-110 transition-transform duration-500">
                <span className="material-symbols-outlined text-[32px]">account_circle</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight leading-none">Authentication</h2>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 mt-1.5">User profile data</p>
              </div>
            </div>
            <div className="space-y-8">
              <div className="space-y-3">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Email Address</label>
                <div className="h-14 w-full rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800 px-6 flex items-center text-slate-900 dark:text-white font-medium cursor-not-allowed shadow-inner">
                  {user?.email ?? '--'}
                </div>
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Assigned Role</label>
                <div className="h-14 w-full rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800 px-6 flex items-center shadow-inner cursor-not-allowed">
                  <span className="px-4 py-1.5 rounded-xl bg-primary/10 dark:bg-primary/20 text-primary text-[10px] font-black uppercase tracking-widest border border-primary/20">
                    {({ SUPER_ADMIN: 'Super Admin', TENANT_ADMIN: 'Account Admin', SALES_REP: 'Sales Rep' } as Record<string, string>)[user?.role as string] ?? (user?.role as string)?.replace('_', ' ') ?? '--'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Dealer Group */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-10 shadow-sm hover:shadow-xl hover:shadow-primary/5 transition-all duration-500 group">
            <div className="flex items-center gap-5 mb-10">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary shadow-inner group-hover:scale-110 transition-transform duration-500">
                <span className="material-symbols-outlined text-[32px]">group_work</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight leading-none">Dealer Group</h2>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 mt-1.5">Network assignment</p>
              </div>
            </div>
            {dealerGroupLoading ? (
              <div className="flex items-center gap-3 py-4">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent animate-spin rounded-full" />
                <span className="text-sm font-medium text-slate-400 dark:text-slate-600">Loading...</span>
              </div>
            ) : dealerGroup ? (
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Group Name</label>
                <div className="h-14 w-full rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800 px-6 flex items-center text-slate-900 dark:text-white font-semibold cursor-not-allowed shadow-inner">
                  {dealerGroup.name}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 gap-3 border border-dashed border-outline-variant/30 rounded-2xl">
                <span className="material-symbols-outlined text-3xl text-on-surface/10">link_off</span>
                <p className="text-[10px] font-black uppercase tracking-widest text-on-surface/20">Not assigned to a dealer group</p>
              </div>
            )}
          </div>

          {/* Compliance Rules */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-10 shadow-sm hover:shadow-xl hover:shadow-amber-500/5 transition-all duration-500 group lg:col-span-2">
            <div className="flex items-center gap-5 mb-2">
              <div className="w-14 h-14 rounded-2xl bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center text-amber-500 shadow-inner group-hover:scale-110 transition-transform duration-500">
                <span className="material-symbols-outlined text-[32px]">rule</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight leading-none">Compliance Rules</h2>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 mt-1.5">Customise what gets flagged</p>
              </div>
            </div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-10 ml-[4.5rem] leading-relaxed max-w-2xl">
              Toggle which checks run on your team's videos. Rules with a list icon let you manage the specific words or phrases they detect.
            </p>

            {!customRulesEnabled ? (
              <div className="flex items-start gap-4 p-6 rounded-2xl bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div className="w-10 h-10 rounded-xl bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-400 dark:text-slate-500 shrink-0">
                  <span className="material-symbols-outlined text-[22px]">lock</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-700 dark:text-slate-300">Custom compliance rules are disabled for your account</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                    Your platform administrator has not enabled custom rule overrides for this dealership. Contact your account manager to enable this feature.
                  </p>
                </div>
              </div>
            ) : rulesLoading || listsLoading ? (
              <div className="flex flex-col items-center justify-center py-10 gap-4">
                <div className="w-8 h-8 border-3 border-amber-500 border-t-transparent animate-spin rounded-full" />
                <span className="text-sm font-semibold text-slate-400 dark:text-slate-600 uppercase tracking-widest">Loading rules...</span>
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(FLAG_TYPE_LABELS).map(([flagType, meta]) => {
                  const rule = effectiveRules.find((r) => r.flagType === flagType);
                  const isEnabled = rule?.isEnabled ?? true;
                  const isOverridable = rule?.isOverridable !== false;
                  const resolvedFrom = rule?.resolvedFrom ?? 'PLATFORM';
                  const isTenantOverride = resolvedFrom === 'TENANT';
                  const isToggling = rulesToggling === flagType;
                  const hasListConfig = flagType in LIST_MODAL_META;
                  const isThresholdRule = flagType === 'LOW_VQS_SCORE';
                  const currentThreshold = rule?.config?.threshold ?? 40;

                  return (
                    <div key={flagType} className={`flex items-center justify-between p-5 rounded-2xl border transition-all ${
                      isEnabled
                        ? 'bg-slate-50 dark:bg-slate-950 border-slate-100 dark:border-slate-800'
                        : 'bg-slate-100/50 dark:bg-slate-800/30 border-slate-200 dark:border-slate-700 opacity-60'
                    }`}>
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          meta.severity === 'HARD_BLOCK' ? 'bg-red-500' : 'bg-amber-400'
                        }`} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-bold text-slate-900 dark:text-white text-sm">{meta.label}</p>
                            <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
                              meta.severity === 'HARD_BLOCK'
                                ? 'bg-red-50 dark:bg-red-950/30 text-red-500'
                                : 'bg-amber-50 dark:bg-amber-950/30 text-amber-500'
                            }`}>
                              {meta.severity === 'HARD_BLOCK' ? 'Hard Block' : 'Soft Warning'}
                            </span>
                            {isTenantOverride && (
                              <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                                Override
                              </span>
                            )}
                            {!isOverridable && (
                              <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400">
                                Locked
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-400 dark:text-slate-600 mt-0.5 truncate">{meta.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                        {/* Manage list / threshold button */}
                        {(hasListConfig || isThresholdRule) && (
                          <Tooltip content={isThresholdRule ? `Set score threshold (current: ${currentThreshold})` : `Manage ${meta.label} list`}>
                            <button
                              onClick={() => {
                                if (isThresholdRule) {
                                  setThresholdModalOpen(true);
                                } else {
                                  setActiveModal(flagType as FlagType);
                                }
                              }}
                              className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-primary hover:bg-primary/10 dark:hover:bg-primary/20 transition-all"
                            >
                              <span className="material-symbols-outlined text-[18px]">tune</span>
                            </button>
                          </Tooltip>
                        )}
                        {isTenantOverride && (
                          <Tooltip content="Reset to inherited default">
                            <button
                              onClick={() => handleRuleReset(flagType as FlagType)}
                              disabled={isToggling || !isOverridable}
                              className="h-8 px-3 rounded-lg text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all disabled:opacity-40"
                            >
                              Reset
                            </button>
                          </Tooltip>
                        )}
                        <Tooltip content={isOverridable ? (isEnabled ? 'Disable rule' : 'Enable rule') : 'Locked by platform'}>
                        <div
                          role="switch"
                          aria-checked={isEnabled}
                          onClick={() => { if (!isToggling && isOverridable) handleRuleToggle(flagType as FlagType, isEnabled); }}
                          className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors duration-200 select-none ${
                            isEnabled ? 'bg-primary' : 'bg-slate-300 dark:bg-slate-700'
                          } ${isOverridable && !isToggling ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                        >
                          {isToggling ? (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white animate-spin rounded-full" />
                            </div>
                          ) : (
                            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200 ${
                              isEnabled ? 'translate-x-6' : 'translate-x-0'
                            }`} />
                          )}
                        </div>
                        </Tooltip>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* CRM Integration */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-10 shadow-sm hover:shadow-xl hover:shadow-primary/5 transition-all duration-500 group lg:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-5">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary shadow-inner group-hover:scale-110 transition-transform duration-500">
                  <span className="material-symbols-outlined text-[32px]">integration_instructions</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight leading-none">CRM Integration</h2>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 mt-1.5">Buyer name auto-fetch</p>
                </div>
              </div>
            </div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-10 ml-[4.5rem] leading-relaxed max-w-2xl">
              Connect your CRM to automatically populate buyer names when a VIN is provided during video upload. Requires the <span className="font-bold text-slate-700 dark:text-slate-300">CRM Auto-Fetch</span> feature flag to be enabled.
            </p>

            {crmLoading ? (
              <div className="flex flex-col items-center justify-center py-10 gap-4">
                <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full" />
                <span className="text-sm font-semibold text-slate-400 dark:text-slate-600 uppercase tracking-widest">Loading integrations...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {ALL_PROVIDERS.map((provider) => {
                  const integration = crmIntegrations.find((i) => i.provider === provider);
                  const isConnected = !!integration;
                  return (
                    <div key={provider} className="flex items-center justify-between p-6 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isConnected ? 'bg-primary/10 text-primary' : 'bg-slate-100 dark:bg-slate-900 text-slate-400'}`}>
                          <span className="material-symbols-outlined text-[20px]">{CRM_ICONS[provider]}</span>
                        </div>
                        <div>
                          <p className="font-bold text-slate-900 dark:text-white text-sm">{CRM_LABELS[provider]}</p>
                          {isConnected && integration.lastTestedAt && (
                            <p className="text-[11px] text-slate-400 dark:text-slate-600 mt-0.5">
                              Last tested: {new Date(integration.lastTestedAt).toLocaleDateString()}
                              {integration.lastErrorMessage && <span className="text-red-400 ml-2">— {integration.lastErrorMessage}</span>}
                            </p>
                          )}
                          {!isConnected && (
                            <p className="text-[11px] text-slate-400 dark:text-slate-600 mt-0.5">Not connected</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {isConnected && (
                          <button
                            onClick={() => handleCrmTest(provider)}
                            disabled={crmTesting === provider}
                            className="h-10 px-5 rounded-xl font-bold text-xs uppercase tracking-widest bg-primary/10 dark:bg-primary/20 text-primary hover:bg-primary/20 transition-all disabled:opacity-50"
                          >
                            {crmTesting === provider ? (
                              <div className="w-4 h-4 border-2 border-primary/30 border-t-primary animate-spin rounded-full" />
                            ) : 'Test'}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            if (isConnected) {
                              handleCrmDisconnect(provider);
                            } else {
                              setCrmConnectProvider(provider);
                              setCrmCredentials('');
                            }
                          }}
                          disabled={crmSaving === provider}
                          className={`h-10 px-5 rounded-xl font-bold text-xs uppercase tracking-widest transition-all disabled:opacity-50 ${
                            isConnected
                              ? 'bg-red-50 dark:bg-red-950/30 text-red-500 hover:bg-red-100 dark:hover:bg-red-950/50'
                              : 'bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/20'
                          }`}
                        >
                          {crmSaving === provider ? (
                            <div className="w-4 h-4 border-2 border-current/30 border-t-current animate-spin rounded-full" />
                          ) : isConnected ? 'Disconnect' : 'Connect'}
                        </button>
                      </div>
                    </div>
                  );
                })}

                {crmConnectProvider && (
                  <div className="p-6 rounded-2xl border border-primary/20 bg-primary/5 dark:bg-primary/10 space-y-4">
                    <p className="text-sm font-bold text-slate-900 dark:text-white">
                      Enter credentials for <span className="text-primary">{CRM_LABELS[crmConnectProvider]}</span> as JSON:
                    </p>
                    <textarea
                      value={crmCredentials}
                      onChange={(e) => setCrmCredentials(e.target.value)}
                      rows={4}
                      placeholder={'{\n  "clientId": "...",\n  "clientSecret": "..."\n}'}
                      className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-5 py-4 text-sm font-mono text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none resize-none"
                    />
                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={() => { setCrmConnectProvider(null); setCrmCredentials(''); }}
                        className="h-10 px-5 rounded-xl font-bold text-xs uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleCrmConnect(crmConnectProvider)}
                        disabled={!crmCredentials.trim() || crmSaving === crmConnectProvider}
                        className="h-10 px-5 rounded-xl font-bold text-xs uppercase tracking-widest bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all disabled:opacity-50"
                      >
                        {crmSaving === crmConnectProvider ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin rounded-full" />
                        ) : 'Save Credentials'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DmsSettingsPanel />
        </div>
      </main>

      {/* List Management Modal */}
      {activeModal !== null && (
        <ListManagementModal
          flagType={activeModal}
          wordLists={wordLists}
          requiredPhrases={requiredPhrases}
          mandatoryCount={mandatoryCount}
          saving={saving}
          onSave={handleSaveList}
          onClose={() => setActiveModal(null)}
        />
      )}

      {/* VQS Threshold Modal */}
      {thresholdModalOpen && (
        <ThresholdModal
          currentThreshold={
            effectiveRules.find((r) => r.flagType === 'LOW_VQS_SCORE')?.config?.threshold ?? 40
          }
          saving={saving}
          onSave={handleSaveThreshold}
          onClose={() => setThresholdModalOpen(false)}
        />
      )}
    </div>
  );
};

export default SettingsPage;
